import * as THREE from 'three';
import { Engine } from './core/engine.js';
import { Environment } from './core/env.js';
import { buildWorld } from './world/index.js';
import { Hud, applySiteCopy, applyMusicCredit, isDevHost } from './ui/hud.js';
import { Music } from './ui/music.js';
import { setupTouch, setupFullscreen } from './ui/touch.js';
import { createPortDebug } from './ui/debug-ports.js';
import { RENDER, WORLD, SEA, WIND, ISLANDS, windAt } from './config.js';
import { Sailing } from './world/sailing.js';
import { Fleet } from './net/fleet.js';
import { HerringSchools } from './world/herring.js';
import { Fishing } from './game/fishing.js';
import { Crossing } from './game/ferry.js';
import { Trading } from './game/trade.js';

const canvas = document.getElementById('world');
const loadingScreen = document.querySelector('.loading');
const loadingBar = document.getElementById('loading-bar');
const loadingStatus = document.getElementById('loading-status');

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

async function boot() {
    applySiteCopy(document);
    applyMusicCredit(document);

    const engine = new Engine(canvas);
    const env = new Environment({
        dayLength: RENDER.dayLength,
        startHour: RENDER.startHour,
    });

    // --- generate ---------------------------------------------------------
    const world = await buildWorld(env, (fraction, label) => {
        loadingBar.style.width = `${Math.round(fraction * 100)}%`;
        loadingStatus.textContent = label;
    });

    engine.scene.add(world.scene);
    engine.scene.add(world.sky);

    const span = WORLD.size * WORLD.voxelSize;
    engine.controls.maxDistance = span * 1.15;

    // --- the boat ---------------------------------------------------------
    // You arrive by water: the boat is already sailing when the page opens,
    // some way offshore, with the island ahead of her.
    const sailing = new Sailing(world.sailboat, world.ground, { voxelSize: WORLD.voxelSize });
    // Back where she was left, if she was left anywhere; otherwise stand in
    // from seaward as a first-time visitor does.
    if (world.market?.pier) sailing.obstacles.push(world.market.pier);

    // One way to begin, however you got here. A berth saved in this world is
    // resumed; anything else — a first visit, a berth from before the ground
    // moved, a boat somehow aground — stands in towards the Zaanse Schans,
    // because that is the island with the links on it. Broken and brand new
    // are the same case, which means there is only one path to get wrong.
    const home = ISLANDS.find((i) => i.id === 'zaanse-schans') || ISLANDS[0];
    const standIn = () => sailing.placeApproaching(home, home.size * WORLD.voxelSize * 0.62);
    if (sailing.restore() === 'none') standIn();

    // Open the camera over her shoulder, looking the way she is going, so the
    // island is what you are sailing towards rather than what you start on.
    {
        const astern = new THREE.Vector3(-Math.sin(sailing.heading), 0, -Math.cos(sailing.heading));
        engine.controls.target.copy(sailing.position);
        engine.camera.position.copy(sailing.position)
            .add(astern.multiplyScalar(22))
            .add(new THREE.Vector3(0, 13, 0));
    }

    // Other visitors, if the relay is up. Constructed after the boat so it has
    // something to report, and never awaited — the world does not wait on it.
    const music = new Music(document);

    // --- interaction ------------------------------------------------------
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2(-10, -10);
    const pickTargets = world.links.map((l) => l.pick);

    let hoveredId = null;    // from the 3D scene, follows the pointer
    let hoveredSchool = null;
    let hoveredLandmark = null;
    let hoveredAnchor = null;
    let hoveredPort = null;
    let hoveredWharf = null;
    let hoveredBoat = null;

    const _proj = new THREE.Vector3();
    /** World point to screen pixels, or null when it is behind the camera. */
    const project = (world) => {
        if (!world) return null;
        _proj.copy(world).project(engine.camera);
        if (_proj.z > 1) return null;
        return {
            x: (_proj.x * 0.5 + 0.5) * window.innerWidth,
            y: (-_proj.y * 0.5 + 0.5) * window.innerHeight,
        };
    };
    let focusedId = null;    // from the HUD rail or keyboard
    let selectedId = null;   // committed by a click, and it stays put
    let pointerInside = false;

    const linkById = (id) => world.links.find((l) => l.config.id === id);

    const hud = new Hud(document, world.links, {
        onFocus: (id) => {
            focusedId = id;
            syncSelection();
        },
        onOpen: (id, { fly = false } = {}) => {
            const link = linkById(id);
            if (!link) return;
            // Clicking a rail item is unambiguous, so it opens — but it also
            // commits the selection, so the card agrees with what just opened.
            selectedId = id;
            syncSelection();
            if (fly) {
                engine.flyTo(link.worldAnchor.clone().setY(link.group.position.y + 3), 24);
                // Give the flight a beat to read before the tab switches.
                setTimeout(() => window.open(link.config.url, '_blank', 'noopener'), 900);
            } else {
                window.open(link.config.url, '_blank', 'noopener');
            }
        },
        onToggleClock: () => {
            env.paused = !env.paused;
            hud.setClock(env.clockLabel(), env.paused);
        },
        onScrub: (hour) => {
            env.paused = true;
            env.setHour(hour);
            hud.setClock(env.clockLabel(), env.paused);
        },
    });

    // --- herring ----------------------------------------------------------
    // Built before the fleet, because the relay hands the schools straight to
    // them as soon as it says hello.
    const schools = new HerringSchools(
        world.scene,
        // Water, and not a harbour. Herring shoaling between the moored
        // botters is a nice picture and a bad game: the hint to fish and the
        // hint to trade would be fighting over the same square of water.
        (x, z) => sailing.isFloatable(x, z) && !inPort(x, z),
        WORLD.seaLevel * WORLD.voxelSize,
    );
    const fleet = new Fleet(
        world.scene, world.makeBoat, sailing, schools,
        // Fly the colour the relay gave us, or our own boat is the one tjalk
        // on the water whose flag means nothing.
        (flagHex) => world.sailboat.setFlag(world.flagIdFor(flagHex)),
    );
    const fishing = new Fishing(schools, sailing, hud, fleet, world.market);
    // What other boats see of us: the purse, which is the score.
    fleet.catchOf = () => fishing.guilders;

    // The crossing: your boat is the ferry, and the hold it carries herring in
    // is the hold it carries people in.
    // Declared before the schools use it; they only ask once they are
    // spawning, which is long after this has been built.
    const inPort = (x, z) => {
        if (!crossing) return false;
        if (crossing.portAtPoint(x, z)) return true;
        // A clear berth around each quay as well, so a school never sits in
        // the mouth of a harbour somebody is trying to enter.
        return world.ports.some((p) => Math.hypot(x - p.landing.x, z - p.landing.z) < 16);
    };

    const crossing = new Crossing(
        world.scene, world.ports, sailing, hud, fishing, fleet,
        {
            palette: world.palette,
            material: world.material,
            blocks: world.ferryBlocks,
            voxelSize: WORLD.voxelSize,
        },
    );
    fishing.crossing = crossing;
    fleet.crossing = crossing;

    // The goods trade: the same hold and the same purse as everything else.
    const trading = new Trading(sailing, hud, fishing, fleet);
    fishing.trading = trading;
    fleet.trading = trading;

    // P paints the trading areas on the water, on a dev machine. Arguing about
    // hand-tuned numbers from screenshots is slower than looking at them.
    const portDebug = createPortDebug(WORLD.seaLevel * WORLD.voxelSize);
    world.scene.add(portDebug);

    function syncSelection() {
        // A committed selection outranks the pointer, so drifting the cursor
        // off a building — or across another one — does not throw it away.
        const id = selectedId || hoveredId || focusedId;
        hud.setActive(id, { sticky: Boolean(selectedId) && id === selectedId });
        const link = linkById(id);
        engine.outline.selectedObjects = link ? [link.group] : [];
        canvas.style.cursor = hoveredId ? 'pointer' : 'grab';
    }

    window.addEventListener('pointermove', (e) => {
        pointerInside = true;
        pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
        pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    });
    window.addEventListener('pointerleave', () => {
        pointerInside = false;
        pointer.set(-10, -10);
    });

    // A click selects; it never opens a link on its own. Opening is a second,
    // deliberate click on the card — losing a tab to a stray click on the
    // scenery is a bad trade for saving one.
    //
    // Orbiting ends in a click event too, so a drag of any distance is not a
    // click at all and leaves the selection alone.
    let pressAt = null;
    canvas.addEventListener('pointerdown', (e) => {
        pressAt = { x: e.clientX, y: e.clientY };
    });
    canvas.addEventListener('pointerup', (e) => {
        if (!pressAt) return;
        const moved = Math.hypot(e.clientX - pressAt.x, e.clientY - pressAt.y);
        pressAt = null;
        if (moved > 6) return;
        selectedId = hoveredId;   // null when the click landed on scenery
        syncSelection();
    });

    // Keyboard: space to fish, arrows for the tiller, Escape to deselect.
    window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            focusedId = null;
            selectedId = null;
            syncSelection();
            return;
        }
        if ((e.key === 'p' || e.key === 'P') && isDevHost()) {
            portDebug.visible = !portDebug.visible;
            return;
        }
        if (e.code === 'Space' || e.key === ' ') {
            // Space scrolls the page and re-presses a focused button.
            e.preventDefault();
            fishing.toggle();
            return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            // OrbitControls would otherwise pan the camera with the arrows,
            // and the browser would scroll the page.
            e.preventDefault();
            sailing.turn = e.key === 'ArrowLeft' ? 1 : -1;
            return;
        }

    });

    setupFullscreen(document);

    // Thumbs: two helm buttons and a hint you can press, on a phone.
    setupTouch(document, {
        onTurn: (turn) => { sailing.turn = turn; },
        onAction: () => fishing.toggle(),
    });

    // Let go of the tiller and she holds her course. Without this the turn
    // never returns to zero and one tap spins her for ever.
    window.addEventListener('keyup', (e) => {
        if (e.key === 'ArrowLeft' && sailing.turn === 1) sailing.turn = 0;
        if (e.key === 'ArrowRight' && sailing.turn === -1) sailing.turn = 0;
    });

    // A window that loses focus mid-turn would otherwise never see the keyup.
    window.addEventListener('blur', () => { sailing.turn = 0; });

    hud.setClock(env.clockLabel(), env.paused);
    // Engine stats stay on the dev machine; visitors just get the hint.
    if (!isDevHost()) hud.hideClockTools();
    // What is actually being drawn this frame, not what exists in the scene:
    // three.js frustum-culls, so the scene total says nothing about the cost
    // of standing where you are standing.
    const devStats = isDevHost();
    let statsTick = 0;
    let aground = 0;
    if (!devStats) hud.setStats(null);

    // --- reveal -----------------------------------------------------------
    loadingScreen.classList.add('is-done');
    setTimeout(() => loadingScreen.remove(), 900);

    // --- loop -------------------------------------------------------------
    let clockTick = 0;

    function frame() {
        requestAnimationFrame(frame);
        const dt = engine.beginFrame();

        // env.update also drives uTime, which is what moves the water — so it
        // always runs. The relay's clock only overrides the hour afterwards,
        // so everybody sails under the same sun however long ago they opened
        // the page. Paused means somebody is holding the light on purpose.
        env.update(prefersReducedMotion ? dt * 0.25 : dt);
        // The weather runs off the same clock as the sky, so everyone is
        // sailing the same wind without the relay having to broadcast it.
        const serverNow = fleet.serverNow();
        WIND.direction = windAt(serverNow ?? Date.now());
        // The swell runs with it, so the sea and the mills agree.
        if (world.water?.material?.uniforms?.uWindDir) {
            world.water.material.uniforms.uWindDir.value = WIND.direction;
        }
        if (serverNow !== null && !env.paused) {
            const days = serverNow / 1000 / RENDER.dayLength;
            env.setHour(RENDER.startHour + (days % 1) * 24);
        }
        engine.setNightAmount(env.uniforms.uNight.value);

        // Hover test. Only when the pointer is actually over the canvas —
        // otherwise the auto-orbit keeps re-triggering hovers on its own.
        if (pointerInside) {
            raycaster.setFromCamera(pointer, engine.camera);
            // Buildings, schools of herring and other visitors are all things
            // the pointer can find, so they are all tested together and the
            // nearest one wins.
            const hit = raycaster.intersectObjects(
                [
                    ...pickTargets,
                    ...schools.pickTargets(),
                    ...fleet.pickTargets(),
                    ...world.landmarks,
                    ...world.ports.map((p) => p.pick),
                    ...world.wharves.map((q) => q.pick),
                ],
                false,
            )[0];
            const data = hit?.object.userData;

            const id = data?.site ? data.site.id : null;
            if (id !== hoveredId) {
                hoveredId = id;
                syncSelection();
            }

            hoveredSchool = data?.school || null;
            hoveredBoat = data?.fleetBoat ?? null;
            hoveredLandmark = data?.landmark || null;
            hoveredAnchor = data?.anchor || null;
            hoveredPort = data?.port || null;
            hoveredWharf = data?.wharf || null;
            if (hit && (hoveredSchool || hoveredBoat || hoveredLandmark || hoveredPort
                || hoveredWharf)) {
                canvas.style.cursor = 'pointer';
            }
        }

        // Another visitor's catch, pinned to their boat. Nothing at zero, so
        // an empty net says nothing rather than boasting about it.
        if (hoveredBoat !== null) {
            const count = fleet.catchFor(hoveredBoat);
            hud.showBoatCatch(count, count ? project(fleet.positionOf(hoveredBoat)) : null);
        } else {
            hud.showBoatCatch(0, null);
        }

        // The card is a hover caption and nothing more: no click, no pinned
        // state to get rid of. Point at the water, read the fact, sail on.
        if (hoveredSchool && !hoveredSchool.dying) {
            hud.showFact('Herring', hoveredSchool.fact, project(
                new THREE.Vector3(hoveredSchool.x, WORLD.seaLevel * WORLD.voxelSize + 2, hoveredSchool.z),
            ));
        } else if (hoveredWharf) {
            // A mill wharf says what it is selling, for how much, and how much
            // of it is left — the three things worth knowing before you spend.
            const q = world.wharves.find((x) => x.id === hoveredWharf);
            const m = trading.market[hoveredWharf] || {};
            const left = Math.floor(m.stock ?? 0);
            const shut = trading.closed ? ' Shut till morning.' : '';
            hud.showFact(
                `${q.name} · ${q.from}`,
                `${left > 0 ? `${left} left` : 'Sold out'} at ƒ${(m.buy ?? 0).toFixed(2)} each. `
                    + `Volendam is paying ƒ${(m.sell ?? 0).toFixed(2)}.` + shut,
                project(q.landing.clone().setY(q.landing.y + 4)),
                'crate-icon',
            );
        } else if (hoveredPort) {
            // A quay says what it is paying and who is standing on it — the
            // two things worth knowing before you sail across for them.
            const port = world.ports.find((p) => p.id === hoveredPort);
            const waiting = crossing.queues[hoveredPort] || 0;
            const people = waiting === 1 ? '1 person waiting' : `${waiting} waiting`;
            const shut = crossing.closed ? ' The crossing does not run after dark.' : '';
            const lines = (hoveredPort === 'volendam'
                ? `Herring fetches ƒ${fishing.price.toFixed(1)} a fish at the market here, `
                    + `and the stalls take what the mills send down. `
                    + `${waiting ? people : 'Nobody'} for the crossing.`
                : `${waiting ? people : 'Nobody waiting'} for the crossing to Volendam. `
                    + 'They pay ƒ1 a head.') + shut;
            hud.showFact(
                port?.id === 'volendam' ? 'Volendam quay' : 'Marken landing',
                lines,
                project(port.landing.clone().setY(port.landing.y + 5)),
                hoveredPort === 'volendam' ? 'coin-icon' : 'people-icon',
            );
        } else if (hoveredLandmark) {
            // Each landmark carries where its own card hangs, so this does
            // not have to know one from another.
            hud.showFact(
                hoveredLandmark.title,
                hoveredLandmark.text,
                project(hoveredAnchor),
                hoveredLandmark.icon,
            );
        } else {
            hud.showFact(null, null);
        }

        const activeId = hoveredId || focusedId;
        for (const link of world.links) {
            link.update(dt, {
                hovered: link.config.id === hoveredId,
                focused: link.config.id === focusedId,
            });
        }

        // The mills are not links any more, but they still have to turn: this
        // loop is what drives their sails, and dropping them out of it left
        // three windmills standing dead still on a windy river.
        for (const mill of world.mills) {
            mill.update(dt, { hovered: hoveredLandmark === mill.pick.userData.landmark });
        }

        // Other boats are solid. Refreshed before she moves, from the same
        // interpolated positions that are drawn, so what you see is what you
        // hit.
        sailing.hulls = fleet.hulls();
        sailing.update(dt);
        // Aground through no fault of her own — the same recovery as any other
        // start. Judged on the ground alone: another boat drifting alongside
        // is not grounding, and neither is nudging a jetty, which would
        // otherwise teleport a visitor across the world for touching a quay.
        //
        // And only after a couple of seconds of it, so a moment clipping a
        // bank is a bump rather than a relocation.
        const ashore = !sailing.isFloatable(sailing.position.x, sailing.position.z, {
            hulls: false,
            built: false,
        });
        aground = ashore ? aground + dt : 0;
        if (aground > 2) {
            aground = 0;
            standIn();
        }
        fleet.update(dt);
        schools.update(dt, {
            busy: fishing.busy,
            hovered: hoveredSchool,
            night: env.uniforms.uNight.value,
        });
        fishing.update(dt);
        crossing.update(dt, env.uniforms.uNight.value);
        trading.update(dt, env.uniforms.uNight.value);

        // One card either way: a single row with the herring on it when alone,
        // a flagged row per boat when there is company.
        const board = fleet.scoreboard(fishing.guilders, fishing.hold)
            || [{ flag: 0, count: fishing.guilders, hold: fishing.hold, you: true }];
        hud.setBoard(board, {
            landed: fishing.landed || fishing.sold || crossing.landed || trading.traded,
            people: crossing.passengers,
            crates: trading.count,
        });
        hud.setPrice(fishing.price);
        music.update(dt, env.uniforms.uNight.value);
        // Once the visitor takes the tiller the camera keeps the boat in
        // frame, but gently — it is a following eye, not a chase cam, and it
        // never fights a drag.
        if (sailing.underway && !engine.isFlying) {
            engine.controls.autoRotate = false;
            engine.controls.target.lerp(sailing.position, Math.min(1, dt * 0.8));
        }

        world.clouds.userData.update(dt);
        // The Paard van Marken turns its beam, and only after dark.
        world.marken?.update(dt, env.uniforms.uNight.value);
        world.river.update(env.uniforms.uTime.value, dt, sailing.position);
        hud.updateLabelPosition(engine.camera);

        clockTick += dt;
        if (clockTick > 0.5) {
            clockTick = 0;

            hud.setClock(env.clockLabel(), env.paused);
            if (!env.paused) {
                document.getElementById('clock-scrub').value = env.hour.toFixed(2);
            }
        }

        void activeId;
        engine.render();

        // Read after rendering, not before: the counters are reset at the top
        // of the frame, so anything sampled earlier in it is zero by
        // definition — which is exactly what the stats line was showing.
        statsTick += dt;
        if (devStats && statsTick > 0.5) {
            statsTick = 0;
            hud.setStats(`${Math.round(engine.renderer.info.render.triangles / 1000)}k triangles`);
        }
    }

    frame();
}

boot().catch((err) => {
    console.error(err);
    loadingStatus.textContent = 'The polder flooded. Check the console.';
    loadingStatus.classList.add('is-error');
});
